import { useState } from "react"
import { Button } from "react-bootstrap"
import { useSelector } from "react-redux"
import { accountSelector } from "../../../redux/account/accountSlice"
import { useAppDispatch } from "../../../redux/store"
import { showError, showSuccess } from "../../../redux/counter/counterSlice"
import { useAddNewAdditionalServiceMutation, useGetAdditionalServicesQuery } from "../../../services/bClientApi"
import { AdditionalServiceType } from "../../../Types/additionalServiceType"
import AdditionalServiceEntry from "../../AdditionalService/AdditionalServiceEntry/AdditionalServiceEntry"
import SelectCleaningModal from "../../Modals/SelectCleaningModal/SelectCleaningModal"
import canAccess from "../../../services/accessService/accessService"
import { PropertyBookingEntry } from "../../../Types/bookingTypes"
import { isParent } from "../../../Helpers/Property/isParent"
import { PropertyType } from "../../../Types/propertyTypes"

interface BookingCleaningsProps {
  bookingData: PropertyBookingEntry
  propertyData: PropertyType
  relatedBookings: PropertyBookingEntry[]
  myProperties: { [key: string]: PropertyType }
  cleaningAvailable: boolean
}

export default function BookingCleanings({
  bookingData,
  propertyData,
  relatedBookings,
  myProperties,
  cleaningAvailable,
}: BookingCleaningsProps) {
  const {
    _id: myAccountId,
  } = useSelector(accountSelector)

  const dispatch = useAppDispatch()
  const [addCleaningModal, setAddCleaningModal] = useState(false)
  const [
    addNewAdditionalService
] = useAddNewAdditionalServiceMutation()

  const {
    data: additionalServiceData,
  } = useGetAdditionalServicesQuery({
    additionalServicesQuery: {
      bookingId: bookingData._id,
      serviceType: 'cleaning',
      // status: ['completed', 'in-progress', 'confirmed', 'pending', 'new'],
      status: ['new', 'pending', 'started', 'completed', 'ready'],
    },
    pagination: {
      limit: 100,
      page: 1,
    }
  }, {
    skip: !bookingData._id,
  })

  const additionalServicesList = additionalServiceData?.docs || []

  const handleAddCleaning = async (cleaningData: { cleaningType: 'full' | 'basic', serviceDate: Date }) => {
    try {
      const newCleaning: Partial<AdditionalServiceType> = {
        serviceDate: cleaningData.serviceDate,
        createdDate: new Date(),
        createdBy: myAccountId || '',
        propertyId: bookingData.propertyId,
        bookingId: bookingData._id,
        serviceType: 'cleaning',
        serviceSubType: cleaningData.cleaningType,
        price: 0,
        status: 'new',
        customerNote: '',
        internalNote: 'Added via booking page',
      }

      const newCleanings = [newCleaning]

      // if there are related bookings, add cleanings to them as well
      if (relatedBookings && relatedBookings.length > 0) {
        relatedBookings.forEach((relatedBooking: PropertyBookingEntry) => {

          // if related booking is a parent, skip it
          if (isParent(myProperties[relatedBooking.propertyId], myProperties[bookingData.propertyId])) {
            return
          }

          const relatedCleaning = { ...newCleaning }
          if (relatedBooking._id !== bookingData._id) {
            relatedCleaning.bookingId = relatedBooking._id
            relatedCleaning.propertyId = relatedBooking.propertyId
            newCleanings.push(relatedCleaning)
          }
        })
      }

      for (const cleaning of newCleanings) {
        const result = await addNewAdditionalService(cleaning).unwrap()
        if (result && result._id) {
          dispatch(showSuccess('Additional cleaning added successfully'))
        } else {
          dispatch(showError('Error adding cleaning service'))
        }
      }

    } catch (error) {
      console.error('Error adding cleaning:', error)
      dispatch(showError('Error adding cleaning service'))
    }
  }

  if (!cleaningAvailable || !canAccess('routes', 'additional-service')) {
    return null
  }

  return (
    <div className="mb-4">
      <h3>Additional Cleaning Services</h3>

      {canAccess('routes', 'add-additional-service') && bookingData.checkIn && bookingData.checkOut && (
        <Button
          className="mb-3"
          variant="primary"
          onClick={() => setAddCleaningModal(true)}
        >
          Add Additional Cleaning
        </Button>
      )}

      {/* Render additional cleaning service entries here */}
      {additionalServicesList.length === 0 && (
        <p>No additional cleaning services found for this booking.</p>
      )}
      {additionalServicesList.map((service: AdditionalServiceType) => (
        <div key={service._id} className="mb-3">
          <AdditionalServiceEntry
            additionalServiceData={service}
            showActions={true}
          />
        </div>
      ))}

      <SelectCleaningModal
        active={addCleaningModal}
        onClose={() => setAddCleaningModal(false)}
        onSelect={handleAddCleaning}
        bookingCheckIn={bookingData.checkIn}
        bookingCheckOut={bookingData.checkOut}
        existingCleaningDates={additionalServicesList.map((service: AdditionalServiceType) => service.serviceDate)}
        title="Add Additional Cleaning"
        message="Select the cleaning type and date for the additional cleaning service."
      />
    </div>
  )
}
